<!-- eslint-disable vue/no-v-for-template-key -->
<template>
<div>
    <div class="custom-slider-holder">
    <ul ref="customSlider" :class="[parentCustomClass, 'custom-slider']">
      <template v-for="(slide, index) in slides" :key="index">
        <li
          :class="[slideCustomClass ? slideCustomClass : 'custom-slider-slide']"
        >
          <slot name="main-slide" :item="slide" :index="index"></slot>
        </li>
      </template>
    </ul>
  </div>
  <!-- Navigation -->
  <div
    v-if="slides.length > 1 && navigationActive"
    class="carousel__navigation carousel__navigation-static custom-slider-nav"
  >
    <button
      :class="[
        'carousel__prev',
        currentIndex > 0 ? '' : 'pointer-events-none opacity-50',
      ]"
      aria-label="Previous Slide"
      @click="moveSlide(-1)"
    >
      <div v-html="previousIconHtml"></div>
    </button>
    <span
      v-if="showPagination"
      class="slide-index carousel_pagination-count desktop-body-l"
      >{{ currentIndex + 1 }} of {{ slides.length }}</span
    >
    <button
      :class="[
        'carousel__next',
        currentIndex < slides?.length - 1
          ? ''
          : 'pointer-events-none opacity-50',
      ]"
      aria-label="Next Slide"
      @click="moveSlide(1)"
    >
      <div v-html="nextIconHtml"></div>
    </button>
  </div>
</div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';
import { isMobile} from 'mobile-device-detect';

const props = withDefaults(
  defineProps<{
    slides: Array<any>;
    previousIconHtml?: string;
    nextIconHtml?: string;
    selectedSlide?: number;
    parentCustomClass?: string;
    slideCustomClass?: string;
    thumbnailCustomClass?: string;
    showPagination?: boolean;
    navigationActive?: boolean;
    isThumbnail?: boolean;
  }>(),
  {
    previousIconHtml: '',
    nextIconHtml: '',
    selectedSlide: 0,
    parentCustomClass: '',
    slideCustomClass: '',
    showPagination: false,
    navigationActive: false,
    isThumbnail: false,
  }
);
const {
  slides,
  previousIconHtml,
  nextIconHtml,
  selectedSlide,
  parentCustomClass,
  showPagination,
  navigationActive,
  slideCustomClass,
  isThumbnail,
} = toRefs(props);
const emit = defineEmits(['update:currentSlide']);
const currentIndex = ref<number>(0);
const customSlider = ref({} as HTMLElement);
const touchStartX = ref(0);
const touchEndX = ref(0);
const slideWidth = ref(0);

onMounted(() => {
  window.addEventListener('resize', recalculateWidth);
  recalculateWidth();
  if (isMobile) {
    customSlider.value.ontouchstart = onTouchStart;
  } else {
    customSlider.value.onmousedown = onTouchStart;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', recalculateWidth);
});
const recalculateWidth = () => {
  if (customSlider.value && customSlider.value.children.length > 0) {
    slideWidth.value = parseFloat(
      window.getComputedStyle(customSlider.value.children[0]).width
    );
    if (isThumbnail.value) {
      setTranslateForthumbnail();
    } else {
      setTranslate();
    }
  }
};

const setTranslateForthumbnail = () => {
  const staticSlides = Math.floor(
    customSlider.value.clientWidth / 2 / slideWidth.value
  );
  // Starting edge cases
  if (currentIndex.value < staticSlides) {
    customSlider.value.style.transform = `translateX(0px)`;
  } else if (currentIndex.value + staticSlides + 1 > slides.value.length) {
    //ending edge cases
    customSlider.value.style.transform = `translateX(-${
      (slides.value.length - staticSlides - staticSlides) * slideWidth.value
    }px)`;
  } else {
    // middle carousel
    customSlider.value.style.transform = `translateX(-${
      (currentIndex.value - staticSlides) * slideWidth.value
    }px)`;
  }
};

const setTranslate = () => {
  customSlider.value.style.transform = `translateX(-${
    currentIndex.value * slideWidth.value
  }px)`;
};
const getTranslateXValue = () => {
  return new DOMMatrix(customSlider.value.style.transform).m41;
};
const moveSlide = (direction: number) => {
  if (
    (currentIndex.value === 0 && direction === -1) ||
    (currentIndex.value === slides.value.length - 1 && direction === 1)
  )
    return;
  slideWidth.value = parseFloat(
    window.getComputedStyle(customSlider.value.children[0]).width
  );
  currentIndex.value = currentIndex.value + direction;
  if (isThumbnail.value) {
    setTranslateForthumbnail();
  } else {
    setTranslate();
  }
};
watch(currentIndex, (value) => {
  if (value !== selectedSlide.value) {
    emit('update:currentSlide', value);
  }
});
watch(selectedSlide, (value) => {
  if (value !== currentIndex.value) {
    currentIndex.value = selectedSlide.value;
    recalculateWidth();
  }
});

const onTouchStart = (event) => {
  if (!isMobile) {
    event.preventDefault();
    touchStartX.value = parseFloat(event.clientX);
    customSlider.value.style.transition = 'none';
    document.onmouseup = onTouchEnd;
    document.onmousemove = onTouchMove;
  } else {
    touchStartX.value = parseFloat(event.touches[0].clientX);
    document.ontouchend = onTouchEnd;
    document.ontouchmove = onTouchMove;
  }
};

const onTouchMove = (event) => {
  if (!isMobile) {
    touchEndX.value = parseFloat(event.clientX);
  } else {
    touchEndX.value = parseFloat(event.touches[0].clientX);
  }
  const moveDistance = touchStartX.value - touchEndX.value;
  if (isThumbnail.value && Math.abs(moveDistance) > 20) {
    const staticSlides = Math.floor(
      customSlider.value.clientWidth / 2 / slideWidth.value
    );
    customSlider.value.style.transform = `translateX(-${Math.min(
      Math.abs(getTranslateXValue()) + moveDistance,
      slideWidth.value * (slides.value.length - 2 * staticSlides)
    )}px)`;
  }
};
const onTouchEnd = () => {
  if (
    !isThumbnail.value &&
    touchEndX.value &&
    touchStartX.value - touchEndX.value > 50
  ) {
    moveSlide(1);
  } else if (
    !isThumbnail.value &&
    touchEndX.value &&
    touchEndX.value - touchStartX.value > 50
  ) {
    moveSlide(-1);
  }
  customSlider.value.style.transition = 'transform 0.5s ease-in-out';
  removeEventListeners();
  touchStartX.value = 0;
  touchEndX.value = 0;
};

const removeEventListeners = () => {
  if (isMobile) {
    document.ontouchend = null;
    document.ontouchmove = null;
  } else {
    document.onmouseup = null;
    document.onmousemove = null;
  }
};
</script>

<style lang="scss">
.custom-slider {
  @apply m-0 p-0;
}

.custom-slider-holder {
  @apply relative overflow-hidden;
}

.custom-slider {
  transition: transform 0.5s ease-in-out;
  @apply flex;

  &-slide {
    @apply w-full shrink-0;
  }
}

.modal-dialog {
  .custom-slider-nav {
    &.carousel__navigation {
      @apply my-0 -mx-6 lg:-mx-14 bottom-auto top-1/2 -translate-y-1/2 justify-between;

      .carousel__prev,
      .carousel__next {
        @apply w-[30px] h-[30px];
      }
    }
  }
}

.thumbnail {
  &-slides {
    @apply py-4;
  }

  &-slide {
    @apply lg:w-[22.22%] xl:w-[13.33%] xxxxl:w-[8.69%] flex-shrink-0;
  }
}

</style>
